import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, setTokens, clearTokens, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null); // null = checking, false = anonymous
  const [ready, setReady] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setSession(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => setSession(data))
      .catch(() => {
        clearTokens();
        setSession(false);
      })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setTokens(data);
    // Nunca debe quedar en caché un dato de sesión previa: cada login parte de cero, incluso
    // si es otro rol dentro de la misma pestaña (evita fugas de datos entre roles).
    qc.clear();
    setSession({ user: data.user, permissions: data.permissions, hidden_fields: data.hidden_fields });
    return data.user;
  }, [qc]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* sesión ya inválida */
    }
    clearTokens();
    qc.clear();
    setSession(false);
  }, [qc]);

  const patchUser = useCallback((user) => {
    setSession((prev) => (prev ? { ...prev, user } : prev));
  }, []);

  const can = useCallback(
    (module, action = "read") => Boolean(session?.permissions?.[module]?.includes(action)),
    [session]
  );

  const value = useMemo(
    () => ({
      session,
      ready,
      user: session?.user || null,
      role: session?.user?.rol || null,
      permissions: session?.permissions || {},
      can,
      login,
      logout,
      patchUser,
    }),
    [session, ready, can, login, logout, patchUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
};
